import axios from "axios";
import { Toast } from "../components/common/Toast";
import { ACCESS_TOKEN, ADMIN_CONTACT_CHANGE_REQUEST_URL, ADMIN_CONTACT_CHANGE_VERIFY_URL, ADMIN_TWO_FACTOR_DISABLE_URL, ADMIN_TWO_FACTOR_SETUP_URL, ADMIN_TWO_FACTOR_STATUS_URL, ADMIN_TWO_FACTOR_VERIFY_URL, GET_ADMIN_SESSION, LOGIN_URL, PROFILE } from "../helpers/Constant";

const INVALID_TOKEN_MESSAGE = "Invalid token";

function getStoredAccessToken() {
    const accessToken = localStorage.getItem(ACCESS_TOKEN);

    if (!accessToken || accessToken === "undefined" || accessToken === "null") {
        return "";
    }

    return accessToken;
}

export function getAuthHeader() {
    const accessToken = getStoredAccessToken();

    if (!accessToken) {
        return { headers: {} };
    }

    return { headers: { Authorization: `Bearer ${accessToken}` } };
}

function readErrorMessage(error) {
    const payload = error?.response?.data;

    if (typeof payload === "string") {
        return payload;
    }

    if (payload?.message) {
        return payload.message;
    }

    if (payload?.error) {
        return payload.error;
    }

    return error?.message || "Request failed";
}

function isInvalidTokenError(error) {
    const message = readErrorMessage(error).toLowerCase();
    return message.includes("invalid token") || error?.response?.status === 401;
}

function handleInvalidToken() {
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(PROFILE);

    if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.replace("/login");
    }
}

const api = {
    getAllData: ({ url, setLoading, params }, next) =>
        apiCall({ url, setLoading, params, method: "get" }, next),
    getSingleData: ({ url, setLoading }, next, onError) =>
        apiCall({ url, setLoading, method: "get" }, next, onError),
    createData: ({ url, setLoading, body }, next, onError) =>
        apiCall({ url, setLoading, body, method: "post" }, next, onError),
    updateData: ({ url, setLoading, body }, next, onError) =>
        apiCall({ url, setLoading, body, method: "put" }, next, onError),
    deleteData: ({ url, setLoading }, next) =>
        apiCall({ url, setLoading, method: "delete" }, next),
    auth: {
        login: ({ body, setLoading }, next) =>
            apiCall({ url: LOGIN_URL, setLoading, body, method: "post", includeAuth: false }, next),

        getProfile: (setLoading, next, onError) =>
            apiCall({ url: GET_ADMIN_SESSION, setLoading, method: "get" }, next, onError),

        getAdminSession: (setLoading, next, onError) =>
            apiCall({ url: GET_ADMIN_SESSION, setLoading, method: "get" }, next, onError),

        getTwoFactorStatus: (setLoading, next, onError) =>
            apiCall({ url: ADMIN_TWO_FACTOR_STATUS_URL, setLoading, method: "get" }, next, onError),

        getTwoFactorSetup: (setLoading, next, onError) =>
            apiCall({ url: ADMIN_TWO_FACTOR_SETUP_URL, setLoading, method: "get" }, next, onError),

        verifyTwoFactor: ({ code, setLoading }, next, onError) =>
            apiCall({ url: ADMIN_TWO_FACTOR_VERIFY_URL, setLoading, body: { code }, method: "post" }, next, onError),

        disableTwoFactor: ({ code, setLoading }, next, onError) =>
            apiCall({ url: ADMIN_TWO_FACTOR_DISABLE_URL, setLoading, body: { code }, method: "post" }, next, onError),

        requestContactChange: ({ body, setLoading }, next, onError) =>
            apiCall({ url: ADMIN_CONTACT_CHANGE_REQUEST_URL, setLoading, body, method: "post" }, next, onError),

        verifyContactChange: ({ body, setLoading }, next, onError) =>
            apiCall({ url: ADMIN_CONTACT_CHANGE_VERIFY_URL, setLoading, body, method: "post" }, next, onError)
    },
    nexAction: (response, callback) => {
        if (response) {
            callback();
        }
    }
};

const apiCall = async (
    { setLoading, url, body, params, method, includeAuth = true },
    next,
    onError
) => {
    const updateLoading = typeof setLoading === "function" ? setLoading : () => {};
    const axiosCall = () => {
        const authConfig = includeAuth ? getAuthHeader() : { headers: {} };

        if (["post", "put"].includes(method)) {
            return axios[method](url, body, { ...authConfig, params });
        }

        return axios[method](url, { ...authConfig, params });
    };

    try {
        updateLoading(true);
        const response = await axiosCall();
        next(response);
        updateLoading(false);
        return response;
    } catch (error) {
        const errorMessage = readErrorMessage(error);

        if (isInvalidTokenError(error)) {
            handleInvalidToken();
        }

        Toast("error", "Error", errorMessage || INVALID_TOKEN_MESSAGE);
        updateLoading(false);
        if (onError) {
            onError(error);
        }
    }
};

export default api;
